import { dbFirestore } from "firestore";
import useSWRImmutable from "swr/immutable";

import type { FeatureFlag } from "firestore/types";

type FeatureFlagKey =
  | "llhls"
  | "MMSForVOD"
  | "signedUrl"
  | "freePurchase"
  | "polling"
  | "commentPersonalBlock"
  | "useTokenForCast";

const DEFAULT_FLAG: FeatureFlag = {
  globalEnabled: false,
  events: {},
};

/**
 * rolloutPercent の抽選値 (0以上100未満) をキー単位に保持する。
 * 抽選はページロードごとに1回。同じキーを読むコンポーネント間で判定が食い違ったり、
 * 再レンダーのたびに判定が反転したりしないようにするため。
 */
const rolloutDraws = new Map<FeatureFlagKey, number>();

const drawFor = (key: FeatureFlagKey): number => {
  const existing = rolloutDraws.get(key);
  if (existing !== undefined) {
    return existing;
  }
  const draw = Math.random() * 100;
  rolloutDraws.set(key, draw);
  return draw;
};

/**
 * フラグの有効/無効を判定する。
 * globalEnabled または events[eventId] でターゲットになった場合に、
 * さらに rolloutPercent の確率(%)で絞り込む。rolloutPercent 未設定は100%扱い。
 *
 * @param rolloutDraw 0以上100未満の抽選値。rolloutDraw < rolloutPercent なら対象
 */
const isFeatureFlagEnabled = (
  flag: FeatureFlag,
  eventId: string | undefined,
  rolloutDraw: number
): boolean => {
  const isTargeted =
    flag.globalEnabled || Boolean(eventId && flag.events[eventId]);
  if (!isTargeted) {
    return false;
  }
  if (flag.rolloutPercent === undefined) {
    return true;
  }
  // 0〜100 のドメインルールはスキーマでなくここで担保し、範囲外の値は近い側の端に丸める
  const rolloutPercent = Math.min(100, Math.max(0, flag.rolloutPercent));
  return rolloutDraw < rolloutPercent;
};

/**
 * Firestoreの featureFlags コレクションからフラグを取得し、
 * eventId に応じて有効/無効を判定する汎用hook。
 *
 * @param key Firestoreのドキュメントキー
 * @param eventId 対象のイベントID
 * @param urlOverrides URLパラメータによるオーバーライド設定
 */
export const useFeatureFlagFromFirestore = (
  key: FeatureFlagKey,
  eventId: string | undefined,
  urlOverrides?: { enable?: string; disable?: string }
): boolean | undefined => {
  const { data: flag } = useSWRImmutable<FeatureFlag>(
    `featureFlag:${key}`,
    async () => {
      try {
        const result = await dbFirestore.featureFlags().doc(key).get();
        const data = result.data();
        const returnValue = data ?? DEFAULT_FLAG;

        return returnValue;
      } catch {
        return DEFAULT_FLAG;
      }
    }
  );

  try {
    if (urlOverrides && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (urlOverrides.enable && params.get(urlOverrides.enable) === "1") {
        return true;
      }
      if (urlOverrides.disable && params.get(urlOverrides.disable) === "1") {
        return false;
      }
    }

    if (flag === undefined) {
      return undefined;
    }

    return isFeatureFlagEnabled(flag, eventId, drawFor(key));
  } catch {
    return false;
  }
};
